import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal } from '@angular/core';
import { email, form, FormField, FormRoot, pattern, required, validate } from '@angular/forms/signals';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserDetails } from '../../../auth0/models';
import { Auth0Store } from '../../../auth0/store';

import { KeyValuePipe } from '@angular/common';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { AvatarComponent } from '../../../../shared/components/avatar/avatar.component';
import { DotsLoaderComponent } from '../../../../shared/components/dots-loader/dots-loader.component';
import { ClickCursorDirective } from '../../../../shared/directives/click-cursor.directive';
import { blacklistedEmailDomain } from '../../../auth0/utils/email-domain-blacklist';

import { SnakeCaseTitlePipe } from '../../../../shared-lazy/pipes/snakeCaseTitle.pipe';

const EMAIL_DOMAIN_ERROR = { kind: 'emailDomain' } as const;

@Component({
  selector: 'gnx-invite-member-popup',
  templateUrl: './invite-member-popup.component.html',
  styleUrls: ['./invite-member-popup.component.scss'],
  imports: [
    ClickCursorDirective,
    FormField,
    FormRoot,
    MatTooltip,
    AvatarComponent,
    KeyValuePipe,
    DotsLoaderComponent,
    MatSelect,
    MatOption,
    SnakeCaseTitlePipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InviteMemberPopupComponent implements OnInit {
  private dialogReg = inject<MatDialogRef<InviteMemberPopupComponent>>(MatDialogRef);
  data = inject<UserDetails>(MAT_DIALOG_DATA);

  protected auth0Store = inject(Auth0Store);
  readonly USER_TYPE = { admin: 'Admin', member: 'Member' };
  protected inviteEmail = signal('');

  protected inviteEmailField = form(this.inviteEmail, (path) => {
    email(path);
    pattern(path, /^\S*[@]\S*[.]\S*$/);
    required(path);
    validate(path, ({ value }) => (blacklistedEmailDomain(value()) ? EMAIL_DOMAIN_ERROR : undefined));
  });

  usersList: { email: string; admin: boolean; role?: string }[] = [];
  sendingInvitations: boolean;

  get showApplicativeRoles(): boolean {
    return this.data?.org_config?.userConfig?.rolesAndPermissionsConfig?.active ?? false;
  }

  constructor() {
    effect(() => {
      const inProgress = this.auth0Store.inviteUsersInProgress();
      if (inProgress && this.sendingInvitations) {
        this.onClose();
      }
    });
  }

  ngOnInit(): void {
    if (this.showApplicativeRoles) {
      this.auth0Store.getApplicativeRoles();
    }
  }

  onClose() {
    this.dialogReg.close();
  }

  onSendInvitations() {
    this.sendingInvitations = true;
    this.auth0Store.inviteUsers({
      emails: this.usersList.map((user) => ({ email: user.email, admin: user.admin, role: user.role })),
      fromPopup: true,
      fromSettings: true,
    });
  }

  addUserToList(tooltip: MatTooltip) {
    if (!this.usersList.find((user) => user.email === this.inviteEmail())) {
      this.usersList.push({ email: this.inviteEmail(), admin: false });
      this.inviteEmail.set('');
    } else {
      tooltip.show();
    }
  }

  deleteMember(email: string) {
    this.usersList = this.usersList.filter((user) => user.email !== email);
  }
}
